// core
import React, { useEffect, useState } from "react";

// components
import LModal from "../../../../common/components/Modals/LModal/LModal";
import ModalHeader from "../../../../common/components/Modals/ModalHeader/ModalHeader";
import LifeCompareBarGraph from "../../LifeCoverAmountBarGraph/LifeCompareBarGraph";
import LifeCompareBarGraphLegend from "../../LifeCoverAmountBarGraph/LifeCompareBarGraphLegend";
import Icon from "../../../../common/components/Icon/Icon";

import { getLifeCoverAmounts } from "../../../api/endpoints/ApiLifeCover";

//hooks
import useIsMounted from "../../../../common/hooks/useIsMounted";
import useErrorLogger from "../../../../common/hooks/useErrorLogger";
import IBarGraphData from "../../../interfaces/IBarGraphData";

interface ILifeCompareCoverAmountsModal {
    isOpen: boolean;
    onClose: () => void;
    clientSettings: IBarGraphData[];
}
interface ICoverAmounts {
    index: number;
    label: string;
    value: number[];
}
interface ILifeCompareData {
    mostPopular: number;
    average: number;
    coverAmounts: ICoverAmounts[];
}

const LifeCompareCoverAmountsModal: React.FC<ILifeCompareCoverAmountsModal> = (props) => {
    const { isOpen, onClose, clientSettings } = props;

    const [data, setData] = useState<ILifeCompareData[]>([]);
    const [isLoading, setLoading] = useState(false);

    const ErrorLogger = useErrorLogger();
    const isMounted = useIsMounted();

    useEffect(() => {
        if (isOpen && !isLoading) {
            setLoading(true);
            Promise.all(
                clientSettings.map((setting) =>
                    getLifeCoverAmounts(
                        100000,
                        1000000,
                        setting.age - 5,
                        setting.age + 5,
                        setting.isSmoker,
                        setting.gender
                    )
                )
            )
                .then((res) => {
                    setData(res);
                })
                .catch((e) => {
                    ErrorLogger.Log(e, {
                        source: "Life Compare Cover Amounts Graph - Fetch Data"
                    });
                    setData(null);
                })
                .finally(() => {
                    isMounted.current && setLoading(false);
                });
        }
    }, [clientSettings, isOpen]);

    const getGraphData = (data: ILifeCompareData[]): ICoverAmounts[][] =>
        data.map((item) => item.coverAmounts);

    const getLegendData = (
        data: ILifeCompareData[]
    ): Omit<ILifeCompareData, "coverAmounts">[] =>
        data.map((item) => {
            return { mostPopular: item.mostPopular, average: item.average };
        });

    return (
        <LModal
            Header={() => (
                <ModalHeader
                    onClose={onClose}
                    title={`How much life cover have other people applied for?`}
                />
            )}
            calcHeightMode="auto"
            width="normal"
            isOpen={isOpen}
            onClose={onClose}
            isLoading={isLoading}>
            {() => {
                return (
                    <React.Fragment>
                        <LifeCompareBarGraphLegend data={getLegendData(data)} />
                        <LifeCompareBarGraph
                            coverAmounts={getGraphData(data)}
                            className="gap-pre-2xl"
                        />
                        <a
                            className="layout-flex-row--justify-centered"
                            href="/article/how-much-life-insurance-do-you-need"
                            target="_blank">
                            <Icon id="AlertInfo" className="space-m" /> More about cover
                            amounts
                        </a>
                    </React.Fragment>
                );
            }}
        </LModal>
    );
};

export default LifeCompareCoverAmountsModal;
